/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  setDoc,
  onSnapshot,
  deleteDoc,
  writeBatch,
  QuerySnapshot,
  DocumentData,
  DocumentReference
} from 'firebase/firestore';
import UserPreferenceSingleton from './userPreferenceSingleton';
import { getFirestoreDb, getRealtimeDb, getFirebaseAuth, getFirebaseStorage } from '../utils/firebase';
import { captureException } from '../services/logService';
import { rootStore } from '../mobx/rootStore';
import { onValue, ref as dbRef, set, remove } from 'firebase/database';
import { getFirebaseUniqueUserId } from './common';
import { getActiveTime } from '../services/timeTrackingServices';
import { FIRESTORE_BATCH_LIMIT } from '../global/constants';
import { getDownloadURL, uploadBytesResumable, deleteObject, ref as storageRef } from '@firebase/storage';
import { nanoid } from 'nanoid';
import { signInWithCustomToken, onIdTokenChanged, onAuthStateChanged, signOut } from 'firebase/auth';

/**
 * In-memory cache of preference document data, keyed by the Firestore document path.
 *
 * The same preference document is read many times per page load from different
 * components, so once a document has been fetched there is no need to issue another
 * getDoc() for it. Entries are kept fresh by invalidation, never by a TTL:
 *   - Per-item docs (projects/views) are invalidated by the active collection listeners
 *     subscribeToWorkspaceProjects / subscribeToWorkspaceViews, which drop the matching
 *     entry on every reported change, and by their local writes.
 *   - The user-root doc (getUserPreferenceData) and the workspace doc
 *     (getUserWorkspacePreferenceData) have no active listener wired up, so they rely on
 *     local-write invalidation only. A change made from another tab/device is therefore
 *     not reflected until the next local write or page reload — acceptable for these
 *     per-user preference docs, which are edited from the same client that reads them.
 *
 * A stored value of `null` means "document does not exist" — caching the miss avoids
 * repeatedly reading an absent document. `map.has(path)` distinguishes a cached miss from
 * a path that has never been read.
 */
const preferenceDataCache = new Map<string, DocumentData | null>();

/**
 * Removes a single preference document from the in-memory cache so the next read
 * re-fetches it from Firestore. Safe to call for paths that are not cached.
 * @param {string} path - The Firestore document path to invalidate.
 */
export const invalidatePreferenceCache = (path: string): void => {
  preferenceDataCache.delete(path);
};

/**
 * Reads a preference document through the in-memory cache. On a cache miss the document
 * is fetched once via getDoc() and stored; subsequent reads are served from memory until
 * a listener or a local write invalidates the path.
 * @param {DocumentReference<DocumentData>} ref - The preference document reference to read.
 * @returns {Promise<DocumentData | null>} The document data, or null if it does not exist.
 */
const readPreferenceDocCached = async (ref: DocumentReference<DocumentData>): Promise<DocumentData | null> => {
  const cachePath = ref.path;
  if (preferenceDataCache.has(cachePath)) {
    return preferenceDataCache.get(cachePath) ?? null;
  }
  const docSnapshot = await getDoc(ref);
  const data = docSnapshot.exists() ? docSnapshot.data() : null;
  preferenceDataCache.set(cachePath, data);
  return data;
};

/**
 * In-memory cache of full preference collection reads, keyed by collection path. Lets repeat
 * getWorkspaceAllItemPreferenceData() calls skip the getDocs() until a write/listener invalidates.
 */
const preferenceCollectionCache = new Map<string, Record<string, DocumentData>>();

/**
 * Drops a cached full-collection read so the next read re-fetches it. Safe for uncached paths.
 * @param {string} path - The Firestore collection path to invalidate.
 */
const invalidatePreferenceCollectionCache = (path: string): void => {
  preferenceCollectionCache.delete(path);
};

/**
 * Updates the user's general preferences in Fire store.
 * @param {any} data - The preference data to update. Will be merged with existing preferences.
 * @returns {Promise<void>} A promise that resolves when the update is complete.
 */
export const updateUserPreference = async (data: any) => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  if (!userDetails?.id) return;
  const userPreferencesRef = doc(getFirestoreDb(), 'user_preferences_v2', userDetails?.id);
  try {
    await setDoc(userPreferencesRef, data, { merge: true });
    // Drop the cached copy so the next read reflects this write. The user-preferences doc
    // is not always covered by an active listener, so we cannot rely on one to invalidate.
    invalidatePreferenceCache(userPreferencesRef.path);
  } catch (error) {
    captureException(error);
  }
};

/**
 * Updates the user's workspace-specific preferences in Fire store.
 * @param {any} data - The workspace preference data to update. Will be merged with existing preferences.
 * @param {string} [workspaceId] - Explicit target workspace. Defaults to the current workspace. Pass this
 *   when a write is scheduled (e.g. debounced) so it lands in the workspace that was active at schedule
 *   time, even if the current workspace has since changed.
 * @returns {Promise<void>} A promise that resolves when the update is complete.
 * @throws Will throw an error if user ID or workspace ID is missing.
 */
export const updateUserWorkspacePreference = async (data: any, workspaceId?: string) => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  const targetWorkspaceId = workspaceId || rootStore.workspaceStore.currentWorkspace?.id;
  if (!userDetails?.id || !targetWorkspaceId) return;
  const userWorkspacePreferencesRef = doc(
    getFirestoreDb(),
    `user_preferences_v2/${userDetails?.id}/workspaces`,
    targetWorkspaceId
  );
  try {
    await setDoc(userWorkspacePreferencesRef, data, { merge: true });
    // No real-time listener covers the workspace-preferences doc, so invalidate on write
    // to guarantee the next read returns fresh data instead of a stale cached copy.
    invalidatePreferenceCache(userWorkspacePreferencesRef.path);
  } catch (error) {
    captureException(error);
  }
};

/**
 * Updates a specific item's preferences within a user's workspace in Firestore.
 * @param {string} itemType - The type of item (e.g., 'views', 'projects').
 * @param {string} itemId - The ID of the item to update preferences for.
 * @param {any} data - The preference data to update. Will be merged with existing preferences.
 * @returns {Promise<void>} A promise that resolves when the update is complete.
 * @throws Will not update if user ID, workspace ID, or item ID is missing.
 *
 * Note (Task 31): this uses setDoc({ merge: true }). Merge can add or change a nested key, but it
 * can't delete one. So passing a partial map like { tasks_filter_type: { order: 1 } } only updates
 * `order` and leaves the other keys as they are — which is why callers can skip the read-before-
 * write. This is safe only while every caller SETS a value. To actually remove a key, pass
 * deleteField() with a dotted path, e.g. { 'tasks_filter_type.order': deleteField() }.
 */
export const updateWorkspaceItemPreference = async (itemType: string, itemId: string, data: any) => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  if (!userDetails?.id || !rootStore.workspaceStore.currentWorkspace?.id || !itemId) return;
  const projectPreferencesRef = doc(
    getFirestoreDb(),
    `user_preferences_v2/${userDetails?.id}/workspaces/${rootStore.workspaceStore.currentWorkspace.id}/${itemType}`,
    itemId
  );
  try {
    await setDoc(projectPreferencesRef, data, { merge: true });
    // Invalidate immediately so a read that races ahead of the listener snapshot still
    // returns the value we just wrote.
    invalidatePreferenceCache(projectPreferencesRef.path);
    // This write also stales the full-collection read for this itemType (parent.path).
    invalidatePreferenceCollectionCache(projectPreferencesRef.parent.path);
  } catch (error) {
    captureException(error);
  }
};

/**
 * Retrieves a specific field from an item's preferences within a user's workspace in Firestore.
 * @param {string} itemType - The type of item (e.g., 'views', 'projects').
 * @param {string} itemId - The ID of the item to get preferences for.
 * @param {string} field - The name of the preference field to retrieve.
 * @returns {Promise<any>} A promise that resolves to the field value, or undefined if not found.
 * @throws Will return undefined if user ID, workspace ID, or item ID is missing.
 */
export const getWorkspaceItemPreferenceData = async (itemType: string, itemId: string, field?: any) => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  if (!userDetails?.id || !rootStore.workspaceStore.currentWorkspace?.id || !itemId) return undefined;
  const userPreferencesRef = doc(
    getFirestoreDb(),
    `user_preferences_v2/${userDetails?.id}/workspaces/${rootStore.workspaceStore.currentWorkspace?.id}/${itemType}`,
    itemId
  );
  try {
    const data = await readPreferenceDocCached(userPreferencesRef);
    if (data) {
      return field ? data[field] : data;
    }
    return undefined;
  } catch (error) {
    captureException(error);
  }
};

/**
 * Retrieves all items' preferences of a specific type from the user's workspace in Firestore.
 * @param {string} itemType - The type of items to retrieve preferences for (e.g., 'views', 'projects').
 * @returns {Promise<any>} A promise that resolves to object containing all item preferences, or undefined if not found.
 * @throws Will return undefined if user ID or workspace ID is missing.
 */
export const getWorkspaceAllItemPreferenceData = async (itemType: string) => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  if (!userDetails?.id || !rootStore.workspaceStore.currentWorkspace?.id) return undefined;
  const userPreferencesRef = collection(
    getFirestoreDb(),
    `user_preferences_v2/${userDetails?.id}/workspaces/${rootStore.workspaceStore.currentWorkspace?.id}/${itemType}`
  );
  const cachePath = userPreferencesRef.path;
  // Serve from the collection cache on a hit; empty collections read back as undefined as before.
  if (preferenceCollectionCache.has(cachePath)) {
    const cached = preferenceCollectionCache.get(cachePath) ?? {};
    return Object.keys(cached).length > 0 ? cached : undefined;
  }
  try {
    const docSnapshot = await getDocs(userPreferencesRef);
    const projectsDataMap: Record<string, DocumentData> = {};
    docSnapshot.forEach((doc) => {
      if (doc.exists()) {
        projectsDataMap[doc.id] = doc.data();
      }
    });
    // Cache the assembled map (empty included) so repeat reads skip the getDocs() until invalidated.
    preferenceCollectionCache.set(cachePath, projectsDataMap);
    return Object.keys(projectsDataMap).length > 0 ? projectsDataMap : undefined;
  } catch (error) {
    captureException(error);
  }
};

/**
 * Updates the user's project-specific preferences within a workspace in Fire store.
 * @param {string} projectId - The ID of the project to update preferences for.
 * @param {any} data - The project preference data to update. Will be merged with existing preferences.
 */
export const updateWorkspaceProjectPreference = async (projectId: string, data: any) => {
  return updateWorkspaceItemPreference('projects', projectId, data);
};

/**
 * Updates the user's view-specific preferences within a workspace in Fire store.
 * @param {string} viewId - The ID of the view to update preferences for.
 * @param {any} data - The view preference data to update. Will be merged with existing preferences.
 */
export const updateWorkspaceViewPreference = async (viewId: string, data: any) => {
  return updateWorkspaceItemPreference('views', viewId, data);
};

/**
 * A single workspace item-preference write, addressed exactly like updateWorkspaceItemPreference().
 */
export interface WorkspacePreferenceUpdate {
  /** The type of item (e.g. 'views', 'projects', 'reports'). */
  itemType: string;
  /** The ID of the item whose preferences are being written. */
  itemId: string;
  /** The preference data to merge into the item's document. */
  data: Record<string, unknown>;
}

/**
 * Commits several workspace item-preference writes in a single Firestore writeBatch instead of
 * issuing one setDoc() network round-trip per change. Every entry targets a document under the
 * current user's workspace (`.../workspaces/{workspaceId}/{itemType}/{itemId}`) and is written with
 * { merge: true }, so the batch behaves exactly like a sequence of updateWorkspaceItemPreference()
 * calls — nested maps are deep-merged, and entries that target the same document are applied in the
 * order given — but the whole set costs one commit. Firestore caps a batch at 500 operations, so
 * entries are chunked to stay within that limit.
 * @param {WorkspacePreferenceUpdate[]} updates - The preference writes to commit.
 * @returns {Promise<void>} A promise that resolves once every batch has been committed.
 * @throws Will not write if user ID or workspace ID is missing; entries missing itemType/itemId are skipped.
 */
export const batchUpdatePreferences = async (updates: WorkspacePreferenceUpdate[]): Promise<void> => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  const workspaceId = rootStore.workspaceStore.currentWorkspace?.id;
  if (!userDetails?.id || !workspaceId) return;
  // Skip entries that can't resolve to a document path so one bad change never blocks the rest.
  const validUpdates = updates?.filter((update) => update?.itemType && update?.itemId);
  if (!validUpdates?.length) return;
  try {
    for (let i = 0; i < validUpdates.length; i += FIRESTORE_BATCH_LIMIT) {
      const batch = writeBatch(getFirestoreDb());
      const writtenRefs: DocumentReference[] = [];
      validUpdates.slice(i, i + FIRESTORE_BATCH_LIMIT).forEach(({ itemType, itemId, data }) => {
        const itemRef = doc(
          getFirestoreDb(),
          `user_preferences_v2/${userDetails.id}/workspaces/${workspaceId}/${itemType}`,
          itemId
        );
        batch.set(itemRef, data, { merge: true });
        writtenRefs.push(itemRef);
      });
      await batch.commit();
      // Invalidate only after the commit resolves — same as updateWorkspaceItemPreference — so a
      // read that races ahead of the listener snapshot still returns the value we just wrote.
      writtenRefs.forEach((itemRef) => {
        invalidatePreferenceCache(itemRef.path);
        // Each write stales its itemType's full-collection read (parent.path); delete() is idempotent.
        invalidatePreferenceCollectionCache(itemRef.parent.path);
      });
    }
  } catch (error) {
    captureException(error);
  }
};

/**
 * Retrieves a specific field from the user's general preferences in Fire store.
 * @param {any} field - The name of the preference field to retrieve.
 * @returns {Promise<any>} A promise that resolves to the field value, or undefined if not found.
 */
export const getUserPreferenceData = async (field: any) => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  if (!userDetails?.id) return undefined;
  const userPreferencesRef = doc(getFirestoreDb(), 'user_preferences_v2', userDetails?.id);
  try {
    const data = await readPreferenceDocCached(userPreferencesRef);
    return data ? data[field] : undefined;
  } catch (error) {
    captureException(error);
  }
};

/**
 * Retrieves a specific field from the user's workspace preferences in Fire store.
 * @param {any} field - The name of the preference field to retrieve.
 * @returns {Promise<any>} A promise that resolves to the field value, or undefined if not found.
 * @throws Will return undefined if user ID or workspace ID is missing.
 */
export const getUserWorkspacePreferenceData = async (field: any) => {
  const userDetails = UserPreferenceSingleton.getInstance().getCurrentUser();
  if (!userDetails?.id || !rootStore.workspaceStore.currentWorkspace?.id) return undefined;
  const userPreferencesRef = doc(
    getFirestoreDb(),
    `user_preferences_v2/${userDetails?.id}/workspaces`,
    rootStore.workspaceStore.currentWorkspace?.id
  );
  try {
    const data = await readPreferenceDocCached(userPreferencesRef);
    return data ? data[field] : undefined;
  } catch (error) {
    captureException(error);
  }
};

/**
 * Retrieves a specific field from the user's project preferences within a workspace in Fire store.
 * @param {string} projectId - The ID of the project to get preferences for.
 * @param {any} field - The name of the preference field to retrieve.
 */
export const getWorkspaceProjectPreferenceData = async (projectId: string, field?: any) => {
  return getWorkspaceItemPreferenceData('projects', projectId, field);
};

/**
 * Retrieves all project preferences from the user's workspace in Fire store.
 */
export const getWorkspaceAllProjectsPreferenceData = async () => {
  return getWorkspaceAllItemPreferenceData('projects');
};

/**
 * Retrieves a specific field from the user's view preferences within a workspace in Fire store.
 * @param {string} viewId - The ID of the view to get preferences for.
 * @param {any} field - The name of the preference field to retrieve.
 */
export const getWorkspaceViewPreferenceData = async (viewId: string, field?: any) => {
  return getWorkspaceItemPreferenceData('views', viewId, field);
};

/**
 * Retrieves all view preferences from the user's workspace in Fire store.
 */
export const getWorkspaceAllViewsPreferenceData = async () => {
  const data = (await getWorkspaceAllItemPreferenceData('views')) || {};
  return data;
};

/**
 * Lifecycle scope a listener is bound to, used to tear listeners down in bulk at the
 * right moment:
 *   - 'workspace': listeners tied to the current workspace/user data — the timer node,
 *     project/view preference collections, and the message-sync trigger document. These
 *     are torn down on a workspace switch (so the next workspace starts clean) and on logout.
 *   - 'auth': Firebase auth/ID-token listeners. These survive a workspace switch (the auth
 *     session is unchanged) and are torn down only on logout.
 */
export type FirebaseListenerScope = 'workspace' | 'auth';

interface TrackedListener {
  scope: FirebaseListenerScope;
  teardown: () => void;
}

/**
 * Centralized registry of every active realtime listener, keyed by its data path so the
 * same path is never subscribed to twice and so listeners can be torn down in bulk by scope.
 *
 * Listener setup functions run again on workspace changes, re-renders, and under React strict
 * mode; without the per-path dedup each call would stack another live subscription on the same
 * path and leak it (and, for the timer node, trigger redundant getActiveTime() work on every
 * update). The scope tagging lets logout/workspace-switch flows unsubscribe the right set of
 * listeners centrally, instead of relying on every call site to remember its own teardown.
 */
const firebaseListenerManager = (() => {
  const listeners = new Map<string, TrackedListener>();

  /**
   * Registers a listener under its path, tearing down any existing listener on the same
   * path first so duplicate subscriptions never accumulate.
   * @param {string} path - The data path uniquely identifying this listener.
   * @param {() => void} unsubscribe - The raw unsubscribe returned by the listener API.
   * @param {FirebaseListenerScope} scope - Lifecycle scope this listener belongs to.
   * @returns {() => void} Teardown that removes the listener and clears its registry entry.
   */
  const register = (path: string, unsubscribe: () => void, scope: FirebaseListenerScope): (() => void) => {
    // Guard: if a listener is already attached for this path, remove it before adding a new one.
    const existing = listeners.get(path);
    if (existing) existing.teardown();

    const teardown = () => {
      unsubscribe();
      // Only clear the entry if this teardown still owns the path; a newer listener may
      // have replaced it (e.g. teardown called twice, or after a re-subscribe).
      if (listeners.get(path)?.teardown === teardown) {
        listeners.delete(path);
      }
    };
    listeners.set(path, { scope, teardown });
    return teardown;
  };

  /** Tears down every listener belonging to the given scope. Safe to call when none exist. */
  const unsubscribeScope = (scope: FirebaseListenerScope): void => {
    // teardown() deletes its own entry from `listeners` mid-iteration; Map.forEach tolerates
    // deleting the current/visited key (it simply won't be revisited), so this is safe.
    listeners.forEach((listener) => {
      if (listener.scope === scope) listener.teardown();
    });
  };

  /** Tears down every active listener regardless of scope. */
  const unsubscribeAll = (): void => {
    // Same in-loop deletion as unsubscribeScope (safe per Map.forEach semantics).
    listeners.forEach((listener) => listener.teardown());
    // Belt-and-suspenders: every teardown already removed its own entry, but clear() guarantees
    // the registry is empty even if a teardown's ownership guard skipped its delete.
    listeners.clear();
  };

  return { register, unsubscribeScope, unsubscribeAll };
})();

/**
 * Registers a workspace-scoped realtime listener under its path. Thin wrapper over the
 * listener manager that keeps the common case (workspace data) terse at the call sites.
 * @param {string} path - The data path uniquely identifying this listener.
 * @param {() => void} unsubscribe - The raw unsubscribe returned by the listener API.
 * @returns {() => void} Teardown that removes the listener and clears its registry entry.
 */
const registerListener = (path: string, unsubscribe: () => void): (() => void) =>
  firebaseListenerManager.register(path, unsubscribe, 'workspace');

/**
 * Tears down every workspace-scoped listener (timer, project/view preferences, message-sync
 * trigger). Call on a workspace switch, before new listeners for the next workspace are created,
 * so reads from the previous workspace stop immediately and never accumulate across switches.
 */
export const cleanupWorkspaceListeners = (): void => {
  firebaseListenerManager.unsubscribeScope('workspace');
};

// Remembers the timer state we last refreshed for, so we only hit getActiveTime()
// when the timer actually starts/stops/changes task — not on every Realtime
// Database tick that re-emits the same value.
let lastTimerStateSignature: string | null = null;

/**
 * Builds a stable signature from only the fields that represent a meaningful
 * start/stop change. Identical values (or repeated emissions) produce the same
 * signature, letting the listener skip redundant API calls.
 */
const getTimerStateSignature = (value: any): string => {
  if (!value) return '';
  return `${value.taskId ?? ''}:${value.status ?? ''}:${value.workspaceId ?? ''}`;
};

/**
 * Subscribes to the user's timer node in Firebase Realtime Database and refreshes
 * the active time only when the timer state actually changes. Guards against
 * duplicate listeners by tearing down any existing subscription on the same path
 * before creating a new one.
 * @returns {() => void} Unsubscribe function that removes the listener.
 */
export const loadFirebaseTimerData = (): (() => void) => {
  try {
    // Reset so the first emission of a fresh subscription always refreshes.
    lastTimerStateSignature = null;
    const uniqueId = getFirebaseUniqueUserId();
    const path = `timer/${uniqueId?.uniqueUserId}`;
    const docRef1 = dbRef(getRealtimeDb(), path);
    const unsubscribe = onValue(docRef1, async (snap) => {
      // Derive the timer state straight from the snapshot and bail out when it
      // matches the last state we acted on, avoiding an API call per tick.
      const signature = getTimerStateSignature(snap.val());
      if (signature === lastTimerStateSignature) return;
      lastTimerStateSignature = signature;
      // Foreground tab already refreshed via its own getActiveTime() call; only the
      // listener refreshes hidden tabs (change came from another tab/device).
      if (typeof document !== 'undefined' && document.visibilityState === 'visible') return;
      await getActiveTime();
    });
    return registerListener(path, () => {
      unsubscribe();
      lastTimerStateSignature = null;
    });
  } catch (e: any) {
    captureException(e);
    return () => {};
  }
};

/**
 * Subscribes to a Firestore collection and invalidates the in-memory preference cache for
 * every changed document before forwarding the snapshot to the caller. Deduplicates by
 * collection path: if a listener is already attached to the same path it is torn down first,
 * so repeated setup calls (workspace changes, re-renders, React strict mode) never stack
 * duplicate subscriptions.
 * @param {string} collectionPath - The Firestore collection path to subscribe to.
 * @param {function} callback - The callback to run on snapshot update.
 * @returns {() => void} Unsubscribe function that removes the listener.
 */
export const subscribeToCollectionWithMap = (
  collectionPath: string,
  callback: (snapshot: QuerySnapshot<DocumentData>) => void
): (() => void) => {
  if (!collectionPath) return () => {};
  const colRef = collection(getFirestoreDb(), collectionPath);
  const unsubscribe = onSnapshot(colRef, (snapshot) => {
    // The snapshot carries the freshest copy of every changed document, so drop the
    // matching cache entries — the next read for those paths will re-fetch fresh data.
    const changes = snapshot.docChanges();
    changes.forEach((change) => invalidatePreferenceCache(change.doc.ref.path));
    // A reported change also stales the cached full-collection read for this path.
    if (changes.length > 0) invalidatePreferenceCollectionCache(collectionPath);
    callback(snapshot);
  });
  return registerListener(collectionPath, unsubscribe);
};

/**
 * Subscribes to changes in the user's workspace projects collection in Firestore.
 * @param {string} userId - The user ID.
 * @param {string} workspaceId - The workspace ID.
 * @param {function} callback - The callback to run on snapshot update.
 * @returns {function} Unsubscribe function.
 */
export const subscribeToWorkspaceProjects = (
  userId: string,
  workspaceId: string,
  callback: (snapshot: QuerySnapshot<DocumentData>) => void
) => {
  if (!userId || !workspaceId) return () => {};
  return subscribeToCollectionWithMap(`user_preferences_v2/${userId}/workspaces/${workspaceId}/projects`, callback);
};

/**
 * Subscribes to changes in the user's workspace views collection in Firestore.
 * @param {string} userId - The user ID.
 * @param {string} workspaceId - The workspace ID.
 * @param {function} callback - The callback to run on snapshot update.
 * @returns {function} Unsubscribe function.
 */
export const subscribeToWorkspaceViews = (
  userId: string,
  workspaceId: string,
  callback: (snapshot: QuerySnapshot<DocumentData>) => void
) => {
  if (!userId || !workspaceId) return () => {};
  return subscribeToCollectionWithMap(`user_preferences_v2/${userId}/workspaces/${workspaceId}/views`, callback);
};

/**
 * Upload a single file to Firebase Storage and return its download URL.
 * @param imageData - The file data object (expects .file property)
 * @param folderName - The folder path in storage
 * @param fileName - Optional custom file name
 */
export const uploadFileToFirebase = async (
  imageData: any,
  folderName: string,
  fileName?: string
): Promise<string | undefined> => {
  try {
    if (!imageData) return;
    const storage = getFirebaseStorage();
    const metadata = { contentType: imageData?.file?.type };
    const fileExtension = imageData?.file?.name.split('.').pop();
    const uniqueFilename = nanoid(8);
    const fullFilename = fileName
      ? fileName
      : `${imageData?.file?.name.replace(/\.[^/.]+$/, '')}_${uniqueFilename}.${fileExtension}`;
    let pathName = '';
    if (folderName !== 'UserProfiles/') {
      const workspace = UserPreferenceSingleton.getInstance().getWorkspace();
      pathName = `workspaces/${workspace?.id}/${folderName}`;
    } else {
      pathName = folderName;
    }
    const sRef = storageRef(storage, pathName + fullFilename);
    const uploadTask = uploadBytesResumable(sRef, imageData?.file, metadata);
    return await new Promise<string>((resolve, reject) => {
      // state_changed fires per chunk; only push whole-percent changes to the store
      let lastReportedPercent = -1;
      uploadTask.on(
        'state_changed',
        (snapshot) => {
          const progress = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100);
          if (progress !== lastReportedPercent) {
            lastReportedPercent = progress;
            rootStore.projectStore.setUploadFilePercent(progress);
          }
        },
        (error) => {
          rootStore.projectStore.setUploadFilePercent(0);
          reject(error);
          dispatchErrorMessage('Getting error for upload file');
        },
        () => {
          getDownloadURL(uploadTask.snapshot.ref).then((downloadURL) => {
            rootStore.projectStore.setUploadFilePercent(0);
            resolve(downloadURL);
          });
        }
      );
    });
  } catch (e) {
    captureException(e);
    dispatchErrorMessage('Getting error for upload file');
  } finally {
    rootStore.projectStore.setUploadFilePercent(0);
  }
};

/**
 * Upload multiple files to Firebase Storage and return their download URLs.
 * @param fileData - Array of file objects ({ file: File })
 * @param folderName - The folder path in storage
 */
export const uploadFilesToFirebase = async (fileData: { file: File }[], folderName: string): Promise<string[]> => {
  try {
    // Track each file's transferred bytes separately so concurrent uploads sum
    // correctly instead of overwriting a single shared counter.
    const bytesPerFile = new Array(fileData?.length ?? 0).fill(0);
    const totalBytes = fileData?.reduce((acc, fileObject) => acc + fileObject.file?.size, 0);
    const storage = getFirebaseStorage();
    // state_changed fires per chunk; only push whole-percent changes to the store
    let lastReportedPercent = -1;
    const promises = fileData?.map((fileObject, fileIndex) => {
      return new Promise<string | null>((resolve, reject) => {
        const metadata = { contentType: fileObject?.file?.type };
        const fileExtension = fileObject?.file?.name.split('.').pop();
        const uniqueFilename = nanoid();
        const fullFilename = `${fileObject?.file?.name.replace(/\.[^/.]+$/, '')}_${uniqueFilename}.${fileExtension}`;
        let pathName = '';
        if (folderName !== 'UserProfiles/') {
          const workspace = UserPreferenceSingleton.getInstance().getWorkspace();
          pathName = `workspaces/${workspace?.id}/${folderName}`;
        } else {
          pathName = folderName;
        }
        const sRef = storageRef(storage, pathName + fullFilename);
        const uploadTask = uploadBytesResumable(sRef, fileObject?.file, metadata);
        uploadTask.on(
          'state_changed',
          (snapshot) => {
            bytesPerFile[fileIndex] = snapshot.bytesTransferred;
            const totalBytesTransferred = bytesPerFile.reduce((acc, bytes) => acc + bytes, 0);
            const progress = totalBytes ? Math.round((totalBytesTransferred / totalBytes) * 100) : 0;
            if (progress !== lastReportedPercent) {
              lastReportedPercent = progress;
              rootStore.projectStore.setUploadFilePercent(progress);
            }
          },
          (error) => {
            reject(error);
            dispatchErrorMessage('Error uploading file');
          },
          () => {
            getDownloadURL(uploadTask.snapshot.ref)
              .then((downloadURL) => resolve(downloadURL))
              .catch((error) => reject(error));
          }
        );
      });
    });
    const uploadedUrls = await Promise.all(promises);
    return uploadedUrls.filter(Boolean) as string[];
  } catch (e) {
    captureException(e);
    dispatchErrorMessage('Error uploading files');
    return [];
  } finally {
    rootStore.projectStore.setUploadFilePercent(0);
  }
};

/**
 * Delete a file from Firebase Storage by its path or URL.
 * @param filePath - The storage path or URL of the file
 */
export const deleteFileFromFirebase = async (filePath: string): Promise<boolean> => {
  try {
    if (!filePath) return false;
    const storage = getFirebaseStorage();
    const fileRef = storageRef(storage, filePath);
    await deleteObject(fileRef);
    return true;
  } catch (error: any) {
    if (error.code === 'storage/object-not-found') {
      console.warn('File does not exist.');
    } else if (error.code === 'storage/unauthorized' || error.code === 'storage/permission-denied') {
      console.warn('User does not have permission to delete this file.');
    } else {
      console.warn('An unexpected error occurred while deleting the file.');
    }
    return false;
  }
};

function dispatchErrorMessage(msg: string) {
  rootStore.alertNotificationStore.setErrorMessage(msg);
}

/**
 * Subscribes to a single Firestore document and calls the callback when it exists.
 * Scoping the listener to one document (instead of the whole collection) avoids a
 * snapshot read for every write across all workspaces/users in the collection.
 * @param {string} collectionName - The Firestore collection name (root-level)
 * @param {string} documentId - The ID of the document to subscribe to
 * @param {(data: any | null) => void} onData - Callback receiving the document data (with id), or null when it does not exist
 * @returns {() => void} Unsubscribe function
 */
export const subscribeToDocument = (
  collectionName: string,
  documentId: string,
  onData: (data: any | null) => void
): (() => void) => {
  try {
    if (!collectionName || !documentId) return () => {};
    const docRef = doc(getFirestoreDb(), collectionName, documentId);
    const unsubscribe = onSnapshot(docRef, (snapshot) => {
      if (snapshot.exists()) {
        onData({ id: snapshot.id, ...snapshot.data() });
      } else {
        onData(null);
      }
    });
    // Track under the workspace scope so logout / workspace-switch cleanup tears this
    // message-sync trigger listener down centrally instead of relying on the caller's teardown.
    return registerListener(docRef.path, unsubscribe);
  } catch (error) {
    captureException(error);
    return () => {};
  }
};

/**
 * Deletes a document from a Firestore collection.
 * @param {string} collectionName - The Firestore collection name
 * @param {string} documentId - The ID of the document to delete
 * @returns {Promise<boolean>} A promise that resolves to true if successful, false otherwise
 */
export const deleteDocumentFromCollection = async (collectionName: string, documentId: string): Promise<boolean> => {
  try {
    if (!collectionName || !documentId) return false;
    const docRef = doc(getFirestoreDb(), collectionName, documentId);
    await deleteDoc(docRef);
    return true;
  } catch (error) {
    captureException(error);
    return false;
  }
};

/**
 * Signs in a user with a custom Firebase token.
 * @param {string} customToken - The custom token to sign in with
 * @returns {Promise<any>} A promise that resolves to the sign-in result
 */
export const signInWithFirebaseCustomToken = async (customToken: string): Promise<any> => {
  try {
    if (!customToken) throw new Error('Custom token is required');
    const result = await signInWithCustomToken(getFirebaseAuth(), customToken);
    return result;
  } catch (error) {
    captureException(error);
    throw error;
  }
};

// Stable registry key for the auth listener pair. setupFirebaseAuthListeners() is called
// multiple times during init (login + workspace setup); registering under a single key lets
// the manager tear down the previous pair before adding a new one, so we never stack
// duplicate onIdTokenChanged + onAuthStateChanged listeners (which would trigger excessive
// token refresh operations).
const AUTH_LISTENERS_KEY = 'auth/listeners';

/**
 * Sets up Firebase authentication listeners for token and auth state changes.
 * Guards against duplicate listeners by tearing down any existing subscription
 * before creating a new one.
 * @returns {() => void} Unsubscribe function for the listeners
 */
export const setupFirebaseAuthListeners = (): (() => void) => {
  try {
    // Register an ID token listener so the Firebase SDK keeps the ID token
    // auto-refreshed in the background; the callback itself is intentionally a no-op.
    const idTokenUnsubscribe = onIdTokenChanged(getFirebaseAuth(), async () => {});

    // Register an auth state listener for parity and lifecycle tracking; no reaction needed.
    const authStateUnsubscribe = onAuthStateChanged(getFirebaseAuth(), async () => {});

    // Register under the auth scope; the manager tears down any existing auth listener
    // pair on this key first, so repeated setup calls never leak duplicates.
    return firebaseListenerManager.register(
      AUTH_LISTENERS_KEY,
      () => {
        idTokenUnsubscribe();
        authStateUnsubscribe();
      },
      'auth'
    );
  } catch (error) {
    captureException(error);
    return () => {};
  }
};

/**
 * Full Firebase teardown for logout: unsubscribes every active listener (workspace and auth
 * scoped alike) and then signs the user out of Firebase auth so no session, token-refresh, or
 * data listener outlives the logout. Safe to call when no listeners are attached.
 * @returns {Promise<void>} Resolves once sign-out completes (or fails and is captured).
 */
export const cleanupAllFirebaseListenersAndSignOut = async (): Promise<void> => {
  firebaseListenerManager.unsubscribeAll();
  try {
    await signOut(getFirebaseAuth());
  } catch (error) {
    captureException(error);
  }
};

/**
 * Sets timer data in Firebase Realtime Database for starting a timer.
 * @param {string} uniqueUserId - The unique user ID for the timer
 * @param {string} taskId - The task ID being tracked
 * @param {string} userId - The user ID
 * @param {string} workspaceId - The workspace ID
 * @param {string} status - The timer status (e.g., 'START')
 * @returns {Promise<boolean>} A promise that resolves to true if successful, false otherwise
 */
export const setTimerData = async (
  uniqueUserId: string,
  taskId: string,
  userId: string,
  workspaceId: string,
  status: string
): Promise<boolean> => {
  try {
    if (!uniqueUserId || !taskId || !userId || !workspaceId) return false;

    await set(dbRef(getRealtimeDb(), `timer/${uniqueUserId}`), {
      taskId: taskId,
      status: status,
      userId: userId,
      workspaceId: workspaceId
    });

    return true;
  } catch (error) {
    captureException(error);
    return false;
  }
};

/**
 * Removes timer data from Firebase Realtime Database for stopping a timer.
 * @param {string} uniqueUserId - The unique user ID for the timer
 * @returns {Promise<boolean>} A promise that resolves to true if successful, false otherwise
 */
export const removeTimerData = async (uniqueUserId: string): Promise<boolean> => {
  try {
    if (!uniqueUserId) return false;

    await remove(dbRef(getRealtimeDb(), `timer/${uniqueUserId}`));

    return true;
  } catch (error) {
    captureException(error);
    return false;
  }
};
